/**
 * What still stands between an organisation and its first WhatsApp message.
 *
 * Connecting the account is the visible step, but it is not the finish line,
 * and pretending otherwise is the single biggest source of confusion in
 * onboarding. Four more things have to happen — an ownership code, a two-step
 * PIN, a payment method added on Meta's side, and a template Meta has
 * approved — and every one of them happens somewhere the wizard never
 * mentions. Customers reach the dashboard believing they are live, schedule a
 * campaign, and watch every message fail.
 *
 * This derives the real state from data that already exists rather than
 * storing a second copy of it. A stored flag would be one more thing to fall
 * out of step with Meta, and Meta is the authority here — a number can be
 * un-verified, restricted or banned long after any flag was written.
 *
 * Mirrors nothing server-side on purpose: this is a view, not a gate. What a
 * number may actually do is decided by `canSendFrom` in whatsapp-phone.utils,
 * which reads platform status alone.
 */

import { WhatsAppAccount, WhatsAppPhoneNumber } from '../services/whatsapp-account.service';
import { canSendFrom, phoneSetupStep } from './whatsapp-phone.utils';

export type SetupTaskId =
  | 'connect'
  | 'verify'
  | 'register'
  | 'payment'
  | 'profile'
  | 'template';

export type SetupTaskState = 'done' | 'todo' | 'blocked' | 'unknown';

export interface SetupTask {
  id: SetupTaskId;
  /** Shown as the row's heading. Plain language, no platform jargon. */
  label: string;
  /** One line saying what the customer actually has to do. */
  detail: string;
  state: SetupTaskState;
  /** Where to go to do it, when Echo can take them there. */
  route?: string;
  /** True when the task must be finished somewhere other than Echo. */
  external?: boolean;
}

export interface SetupProgress {
  tasks: SetupTask[];
  /** Tasks genuinely finished, over tasks that can be judged at all. */
  completed: number;
  total: number;
  /** True once at least one number can actually send. */
  canSend: boolean;
  /**
   * True when nothing is outstanding that Echo can see.
   *
   * Deliberately not "ready to send": the payment method cannot be read back
   * (it needs permissions a Tech Provider does not have), so this can be true
   * while Meta still refuses the first message.
   */
  complete: boolean;
}

/** What the caller knows, beyond the account itself. */
export interface SetupContext {
  /** Any template Meta has approved, from the templates cache. */
  hasApprovedTemplate?: boolean;
  /** Whether the business profile step was completed rather than skipped. */
  hasBusinessProfile?: boolean;
  /**
   * Whether a payment method is known to be on the WABA.
   *
   * Almost always undefined: reading it needs BSP permissions Echo does not
   * hold, which is why the task reports "unknown" and asks rather than tells.
   */
  hasPaymentMethod?: boolean;
}

/**
 * Pick the number the checklist should talk about.
 *
 * An organisation can hold several. The one worth reporting on is whichever is
 * furthest from being usable — saying "ready to send" because the first number
 * is fine, while a second still owes an OTP, is how a number quietly never
 * gets finished.
 *
 * @param phones Every number on the account.
 * @returns The number to report on, or null when there are none.
 */
export function phoneNeedingAttention(
  phones: WhatsAppPhoneNumber[] | undefined
): WhatsAppPhoneNumber | null {
  if (!phones?.length) return null;
  return (
    phones.find(p => phoneSetupStep(p) === 'verify') ??
    phones.find(p => phoneSetupStep(p) === 'register') ??
    phones.find(p => !canSendFrom(p)) ??
    phones[0]
  );
}

/**
 * Work out what is left to do.
 *
 * @param account The organisation's WhatsApp account, if it has one.
 * @param context What the caller knows about templates, profile and billing.
 * @returns The checklist and its totals.
 */
export function setupProgress(
  account: WhatsAppAccount | null | undefined,
  context: SetupContext = {}
): SetupProgress {
  const connected = account?.status === 'connected' && (account.phoneNumbers?.length ?? 0) > 0;
  const phone = phoneNeedingAttention(account?.phoneNumbers);
  const step = phone ? phoneSetupStep(phone) : null;

  const tasks: SetupTask[] = [];

  tasks.push({
    id: 'connect',
    label: 'Connect your WhatsApp number',
    detail: connected ?
      'Connected.' :
      'Link the number you want to message people from.',
    state: connected ? 'done' : 'todo',
    route: '/whatsapp-setup'
  });

  // A Coexistence number is registered already — it came in live from the
  // customer's phone — so neither of the next two applies and showing them
  // would invite calls that can only fail.
  const onBizApp = phone?.isOnBizApp === true;

  if (connected && !onBizApp) {
    tasks.push({
      id: 'verify',
      label: 'Confirm the number is yours',
      detail: step === 'verify' ?
        'Meta will text a code to the number. Enter it in Settings.' :
        'Done.',
      state: step === 'verify' ? 'todo' : 'done',
      route: '/organization/settings'
    });

    tasks.push({
      id: 'register',
      label: 'Set your 6-digit PIN',
      detail: step === 'register' ?
        'Meta needs a standing PIN for the number before it can send.' :
        step === 'verify' ?
          'You can do this once the code above is confirmed.' :
          'Done.',
      // Not "todo" while the code is outstanding: Meta rejects registration
      // until then, with an error naming neither the cause nor the fix.
      state: step === 'register' ? 'todo' : step === 'verify' ? 'blocked' : 'done',
      route: '/organization/settings'
    });
  }

  if (connected) {
    tasks.push({
      id: 'payment',
      label: 'Add a card to Meta',
      detail: context.hasPaymentMethod === true ?
        'Done.' :
        'Messages will not send until Meta has a payment method for this account. ' +
        'This is added in Meta Business Manager, not in Echo.',
      // "unknown" rather than "todo": Echo cannot read payment state back, so
      // claiming it is outstanding would nag customers who have already done it.
      state: context.hasPaymentMethod === true ? 'done' : 'unknown',
      external: true
    });

    tasks.push({
      id: 'profile',
      label: 'Fill in your business details',
      detail: context.hasBusinessProfile ?
        'Done.' :
        'Your name, description and logo, as people see them in WhatsApp.',
      state: context.hasBusinessProfile ? 'done' : 'todo',
      route: '/business-details-setup'
    });

    tasks.push({
      id: 'template',
      label: 'Get a message template approved',
      detail: context.hasApprovedTemplate ?
        'Done.' :
        'Meta has to approve the wording before you can message people first. ' +
        'Approval usually takes minutes, sometimes longer.',
      state: context.hasApprovedTemplate ? 'done' : 'todo',
      route: '/campaign-templates'
    });
  }

  // "unknown" is not progress, and counting it as either done or outstanding
  // would misreport the one task Echo genuinely cannot see.
  const judgeable = tasks.filter(t => t.state !== 'unknown');
  const completed = judgeable.filter(t => t.state === 'done').length;

  return {
    tasks,
    completed,
    total: judgeable.length,
    canSend: (account?.phoneNumbers ?? []).some(p => canSendFrom(p)),
    complete: judgeable.every(t => t.state === 'done')
  };
}
